import classNames from "classnames";
import { AlertTriangle, Info } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { Modal, ModalHeader } from "components/common/modal";
import {
  getBucketLabel,
  getProductionTypeLabel,
  type BenchmarkStatus,
  type BucketComparison,
} from "../../budgetBenchmarks";
import { formatBenchmarkSourceLabel } from "../../budgetBenchmarkDetails";
import type { BenchmarkComparisonSheetProps } from "./BenchmarkComparisonSheet.types";
import {
  countOutlierBuckets,
  formatBenchmarkDelta,
  getClassificationConfidenceMessage,
  getStatusLabel,
  getVisibleBenchmarkBuckets,
} from "./BenchmarkVerdict.utils";

// Typical is within range (success), high is over (danger), low is under (info).
const STATUS_TEXT_STYLES: Record<BenchmarkStatus, string> = {
  typical: "text-success",
  high: "text-danger",
  low: "text-info",
};

const STATUS_BADGE_STYLES: Record<BenchmarkStatus, string> = {
  typical: "border-success/25 bg-success/10 text-success",
  high: "border-danger/25 bg-danger/10 text-danger",
  low: "border-info/25 bg-info/10 text-info",
};

const STATUS_DOT_STYLES: Record<BenchmarkStatus, string> = {
  typical: "bg-success",
  high: "bg-danger",
  low: "bg-info",
};

// Narrow screens put the bar and status on a second line; from xs up each bucket is one line.
const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_3.5rem_4.5rem] items-center gap-x-3 gap-y-2 xs:grid-cols-[5.5rem_minmax(0,1fr)_3rem_4.5rem_5rem]";

const RANGE_BAND_CLASS = "bg-success/30";
const MEDIAN_TICK_CLASS = "bg-foreground/50";

const clampPercent = (value: number) => Math.min(Math.max(value, 0), 100);

function BucketRangeBar({ bucket }: { bucket: BucketComparison }) {
  const bandLeft = clampPercent(Math.min(bucket.range.min, bucket.range.max));
  const bandRight = clampPercent(Math.max(bucket.range.min, bucket.range.max));
  const median = clampPercent(bucket.range.typical);
  const share = clampPercent(bucket.sharePercent);

  return (
    <div
      aria-hidden
      className="relative col-span-2 row-start-2 h-2 rounded-full bg-muted xs:col-span-1 xs:col-start-2 xs:row-start-1"
    >
      <div
        className={classNames("absolute inset-y-0 rounded-full", RANGE_BAND_CLASS)}
        style={{ left: `${bandLeft}%`, width: `${bandRight - bandLeft}%` }}
      />
      <div
        className={classNames(
          "absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full",
          MEDIAN_TICK_CLASS
        )}
        style={{ left: `${median}%` }}
      />
      {/* Keep the whole marker inside the track at 0% and 100%. */}
      <div
        className={classNames(
          "absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card",
          STATUS_DOT_STYLES[bucket.status]
        )}
        style={{ left: `clamp(6px, ${share}%, calc(100% - 6px))` }}
      />
    </div>
  );
}

function BucketRow({ bucket }: { bucket: BucketComparison }) {
  return (
    <div
      role="row"
      className={classNames(ROW_GRID, "border-b border-border/60 py-3 last:border-0")}
    >
      <span role="rowheader" className="truncate text-sm font-medium text-foreground">
        {getBucketLabel(bucket.bucket)}
      </span>
      <BucketRangeBar bucket={bucket} />
      <span
        role="cell"
        className={classNames(
          "text-right text-sm font-semibold tabular-nums",
          STATUS_TEXT_STYLES[bucket.status]
        )}
      >
        {bucket.sharePercent.toFixed(0)}%
      </span>
      <span role="cell" className="text-right text-sm tabular-nums text-muted-foreground">
        {bucket.range.min}–{bucket.range.max}%
      </span>
      <span
        role="cell"
        className="col-start-3 row-start-2 justify-self-end xs:col-start-5 xs:row-start-1"
      >
        <span
          className={classNames(
            "inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums",
            STATUS_BADGE_STYLES[bucket.status]
          )}
        >
          {getStatusLabel(bucket.status)} {formatBenchmarkDelta(bucket.deltaFromTypical)}
        </span>
      </span>
    </div>
  );
}

export const BenchmarkComparisonSheet = ({
  open,
  onOpenChange,
  comparison,
  source,
  onSourceChange,
  peerSampleSize,
  peerBenchmarkAvailable = false,
  onOpenDetails,
  classificationAssessment,
}: BenchmarkComparisonSheetProps) => {
  const buckets = getVisibleBenchmarkBuckets(comparison.buckets);
  const outlierCount = countOutlierBuckets(buckets);
  const productionLabel = getProductionTypeLabel(comparison.productionType);
  const classificationMessage = getClassificationConfidenceMessage(classificationAssessment);
  const close = () => onOpenChange(false);

  return (
    <Modal
      layout="panel"
      isOpen={open}
      onClose={close}
      maxWidth="max-w-lg"
      className="max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] overflow-y-auto"
    >
      <ModalHeader
        layout="panel"
        title="Budget comparison"
        subtitle={`${formatBenchmarkSourceLabel(source)} for ${productionLabel}`}
        onClose={close}
      />

      <div className="flex flex-col gap-3 px-4 pt-4">
        {classificationMessage ? (
          <p className="flex gap-2 rounded-md border border-warning/25 bg-warning/10 px-3 py-2 text-xs leading-snug text-warning">
            <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
            {classificationMessage}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          {outlierCount > 0 ? (
            <span className="rounded-full border border-danger/25 bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
              {outlierCount} outlier{outlierCount === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="rounded-full border border-success/25 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
              Within typical range
            </span>
          )}
          {peerBenchmarkAvailable && onSourceChange ? (
            <div
              role="group"
              aria-label="Benchmark source"
              className="flex shrink-0 items-center gap-0.5 rounded-full border border-border bg-muted/40 p-0.5"
            >
              <button
                type="button"
                aria-pressed={source === "industry"}
                className={classNames(
                  "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                  source === "industry"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => onSourceChange("industry")}
              >
                Industry
              </button>
              <button
                type="button"
                aria-pressed={source === "peers"}
                className={classNames(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                  source === "peers"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => onSourceChange("peers")}
              >
                RivetAI peers
                {peerSampleSize != null ? (
                  <span className="tabular-nums text-muted-foreground">{peerSampleSize}</span>
                ) : null}
              </button>
            </div>
          ) : null}
        </div>

        <div role="table" aria-label="Budget share by category" className="flex flex-col">
          <div
            role="row"
            className={classNames(
              ROW_GRID,
              "border-b border-border pb-2 text-xs font-medium text-muted-foreground"
            )}
          >
            <span role="columnheader">Category</span>
            <span aria-hidden className="hidden xs:block" />
            <span role="columnheader" className="text-right">
              Yours
            </span>
            <span role="columnheader" className="text-right">
              Typical
            </span>
            <span role="columnheader" className="sr-only xs:not-sr-only xs:text-right">
              From median
            </span>
          </div>
          {buckets.map((bucket) => (
            <BucketRow key={bucket.bucket} bucket={bucket} />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border px-4 py-3">
        <ul
          aria-label="Chart key"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
        >
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden className={classNames("h-2 w-4 rounded-full", RANGE_BAND_CLASS)} />
            Typical range
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden className={classNames("h-3 w-0.5 rounded-full", MEDIAN_TICK_CLASS)} />
            Median
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden className="size-2.5 rounded-full bg-foreground" />
            Yours
          </li>
        </ul>
        <Button type="button" color="gray" size="xs" onClick={onOpenDetails}>
          <Info aria-hidden />
          How this works
        </Button>
      </div>
    </Modal>
  );
};
